import React, { useState, useEffect } from "react";
import { FaCalendarAlt, FaUpload } from "react-icons/fa";
import axios from "../../auth/auth";

// const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const MonthlyActivityTab = () => {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);         // upload process
  const [initialLoading, setInitialLoading] = useState(true);
  const [invoiceData, setInvoiceData] = useState([]);
  const [searchPartner, setSearchPartner] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("ALL");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [missingData, setMissingData] = useState({});
  const [approvalAlerts, setApprovalAlerts] = useState([]);
  const [months, setMonths] = useState([]);
  const [customers, setCustomers] = useState([]);



  // ---------------- LOAD DATA ----------------
  const loadUploadedData = async () => {
    if (!selectedMonth) return;

    try {
      const res = await axios.get("/invoice/all_invoices/", {
        params: {
          month: selectedMonth,
          customer: selectedCustomer
        }
      });

      setInvoiceData(res.data.invoices || []);
    } catch (err) {
      console.log("Error loading:", err);
      setInvoiceData([]);
    }
  };


  const loadDropdownData = async () => {
    try {
      const res = await axios.get("/invoice/monthly-uploaded-details");

      setMonths(res.data.months || []);
      setCustomers(res.data.customers || []);
    } catch (err) {
      console.error("Dropdown load failed", err);
    }
  };


  // ---------------- LOAD Monthly dropdown DATA (months and customers)----------------
  // Load dropdown once
  useEffect(() => {
    loadDropdownData();
  }, []);

  // Load data when filters change
  useEffect(() => {
    if (!selectedMonth) return;

    const fetchData = async () => {
      try {
        setInitialLoading(true);
        await loadUploadedData();
      } finally {
        setInitialLoading(false);
      }
    };

    fetchData();
  }, [selectedMonth, selectedCustomer]);


  const customerOptions = ["ALL", ...customers];
  const monthOptions = months;




  // ---------------- UPLOAD ----------------
  const handleUpload = async (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    const formData = new FormData();
    formData.append("file", selected);

    setLoading(true);
    setMessage("");

    //  Reset alerts before each upload
    setApprovalAlerts([]);

    try {
      const res = await axios.post(
        "/invoice/upload_invoice/",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      //  Show approval alerts if any rows were blocked
      if (res.data.alerts && res.data.alerts.length > 0) {
        setApprovalAlerts(res.data.alerts);
        setMessage(""); // Do not show green success message
      } else {
        setMessage(res.data.message || "Upload successful");
      }
      setMissingData(res.data.extra_partners_and_facilities || {});

      // Await the data reload to ensure UI updates before clearing loading
      await loadUploadedData();

    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || "Upload failed. Check file format.";
      setMessage(errorMessage);
    }

    setLoading(false);
  };


  return (
    <div>
      {/* ---------------- HEADER ---------------- */}
      {/* <h2 className="text-2xl font-semibold mb-4">Monthly Activity</h2> */}

      {/* ---------------- UPLOAD CARD ---------------- */}
      <div className="bg-white shadow-lg rounded-xl p-6 mb-6">
        <p className="text-gray-500 mb-4">
          Upload and manage monthly activity reports
        </p>

        <div className="p-5 border rounded-xl bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <FaCalendarAlt className="text-blue-600" />
              Upload Spreadsheet
            </div>

            <label
              className={`px-5 py-2 rounded-lg flex items-center gap-2 text-white cursor-pointer transition
              ${loading ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
            >
              <FaUpload />
              {loading ? "Uploading..." : "Choose File"}
              <input
                type="file"
                className="hidden"
                onChange={handleUpload}
                disabled={loading}
              />
            </label>
          </div>

          {message && (
            <p className="mt-4 text-green-600 font-medium">{message}</p>
          )}



          {/* APPROVAL LOCK ALERTS */}
          {approvalAlerts.length > 0 && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-400 rounded-lg">
              <h4 className="text-yellow-800 font-semibold mb-2">
                Changes made for Approved Invoices
              </h4>

              {approvalAlerts.map((alert, index) => (
                <p key={index} className="text-yellow-700 mb-1">
                  {alert}
                </p>
              ))}
            </div>
          )}



          {loading && (
            <div className="text-center py-4">
              <div className="animate-spin h-7 w-7 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-blue-600 mt-2">Uploading...</p>
            </div>
          )}

          {/* MISSING PARTNERS / GARAGES ALERT */}
          {Object.keys(missingData).length > 0 && (
            <div className="mt-4 p-4 bg-red-50 border border-red-300 rounded-lg">
              <h4 className="text-red-700 font-semibold mb-2">
                Missing Customers / Garages Found
              </h4>

              {Object.entries(missingData).map(([partner, garages], index) => (
                <div key={index} className="mb-2">
                  <p className="font-medium text-red-800">{partner}</p>
                  <ul className="list-disc list-inside text-red-700 ml-4">
                    {garages.map((garage, i) => (
                      <li key={i}>{garage}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>


      {/* ---------------- TABLE CARD ---------------- */}
      <div className="bg-white shadow-lg rounded-xl p-6">
        <h3 className="text-xl font-semibold text-gray-700 mb-3">
          Uploaded Data
        </h3>

        {initialLoading && selectedMonth && (
          <div className="flex justify-center py-6">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {/* SEARCH BOX (ONLY WHEN DATA EXISTS) */}
        {/* FILTERS */}
        <div className="mb-4 flex gap-4 justify-end">

          {/* CUSTOMER DROPDOWN */}
          <select
            value={selectedCustomer}
            onChange={(e) => setSelectedCustomer(e.target.value)}
            className="border px-4 py-2 rounded-lg w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {customerOptions.map((customer, index) => (
              <option key={index} value={customer}>
                {customer === "ALL" ? "All Customers" : customer}
              </option>
            ))}
          </select>

          {/* MONTH DROPDOWN */}
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="border px-4 py-2 rounded-lg w-56 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select Month</option>
            {monthOptions.map((month, index) => (
              <option key={index} value={month}>
                {month}
              </option>
            ))}
          </select>

        </div>
        

        {!selectedMonth && (
          <div className="text-center text-gray-500 py-3">
            Please select a month to view data.
          </div>
        )}


        {selectedMonth && invoiceData.length === 0 ? (
          <div className="text-center text-gray-600 py-10">
            No data found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border rounded-lg">
              <thead className="bg-gray-200">
                <tr>
                  <th className="p-2 border">Month-Year</th>
                  <th className="p-2 border">Partner Name</th>
                  <th className="p-2 border">Partner ID</th>
                  <th className="p-2 border">Facility Name</th>
                  <th className="p-2 border">Facility ID</th>
                  <th className="p-2 border">Reservation Count</th>
                  <th className="p-2 border">Total Transient</th>
                  <th className="p-2 border">Transient Paid</th>
                  <th className="p-2 border">Transient Free</th>
                  <th className="p-2 border">Event Count</th>
                  <th className="p-2 border">Payment Event Count - Cash</th>
                  <th className="p-2 border">Payment Event Count - Card</th>
                  <th className="p-2 border">Permit Count</th>
                  <th className="p-2 border">Passes Count</th>
                  <th className="p-2 border">Total Validations</th>
                  <th className="p-2 border">Total Citations</th>
                  <th className="p-2 border">Charge Citation</th>
                  <th className="p-2 border">Not Charge Citation</th>
                  <th className="p-2 border">Amount Collected</th>
                </tr>
              </thead>

              <tbody>
                {invoiceData.map((row, index) => (
                  <tr key={index} className="text-center">
                    <td className="p-2 border">{row.month_year}</td>
                    <td className="p-2 border">{row.partner_name}</td>
                    <td className="p-2 border">{row.partner_id}</td>
                    <td className="p-2 border">{row.facility_name}</td>
                    <td className="p-2 border">{row.facility_id}</td>
                    <td className="p-2 border">{row.reservation_count}</td>
                    <td className="p-2 border">{row.total_transient_count}</td>
                    <td className="p-2 border">{row.transient_count_with_payment}</td>
                    <td className="p-2 border">{row.transient_count_without_payment}</td>
                    <td className="p-2 border">{row.event_count}</td>
                    <td className="p-2 border">{row.payment_event_count_cash}</td>
                    <td className="p-2 border">{row.payment_event_count_card}</td>
                    <td className="p-2 border">{row.permit_count}</td>
                    <td className="p-2 border">{row.passes_count}</td>
                    <td className="p-2 border">{row.total_validations}</td>
                    <td className="p-2 border">{row.total_citations}</td>
                    <td className="p-2 border">{row.charge_citation}</td>
                    <td className="p-2 border">{row.not_charge_citation}</td>
                    <td className="p-2 border">{row.amount_collected}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyActivityTab;

